import { parseISO } from 'date-fns'
import { UserMinus, UserPlus, Users } from 'lucide-react'
import { useId } from 'react'
import { AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid } from 'recharts'

import { useGetUserAnalytics } from '@/api/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/base/card'
import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	CHART_PALETTE_FILLS,
	CHART_SERIES_PRIMARY_STROKE
} from '@/components/base/chart'
import { Skeleton } from '@/components/base/skeleton'
import { Text } from '@/components/base/text'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { useI18n } from '@/i18n/i18n-react'

import { type AnalyticsInterval, KPICard, resolveComparePeriodParam, useComparisonLabel } from './analytics-shared'

export function UserAnalyticsView({
	interval,
	periodStart,
	comparePeriod
}: {
	interval: AnalyticsInterval
	periodStart?: string
	comparePeriod?: string
}) {
	const { L } = useI18n()
	// Resolve the `lastYear` sentinel to a concrete date for the API; 'none'/specific/omitted pass through.
	const resolvedComparePeriod = resolveComparePeriodParam(interval, periodStart, comparePeriod)
	const { data: analytics, isLoading } = useGetUserAnalytics({
		interval,
		...(periodStart ? { periodStart } : {}),
		...(resolvedComparePeriod ? { comparePeriod: resolvedComparePeriod } : {})
	})

	const overview = analytics?.overview
	const usersOverTime = analytics?.usersOverTime

	const comparisonLabel = useComparisonLabel(overview?.comparisonRange)
	const hasComparison = overview?.hasComparison ?? false

	const activeGradientId = `active-grad-${useId().replace(/:/g, '')}`

	// Hourly buckets arrive as 'yyyy-MM-dd HH:00' and are labelled with just the time.
	const formatPointDate = (date: string) =>
		date.includes(' ') ? (date.split(' ')[1] ?? date) : L.Date.ShortWithDate(parseISO(date).getTime())

	// Split the active-users area into a solid series (completed buckets) and a dashed series (the
	// in-progress trailing bucket); the dashed series carries the last completed point to connect.
	const activeBase =
		usersOverTime?.map(point => ({
			date: formatPointDate(point.date),
			value: point.activeUsers,
			partial: point.partial
		})) ?? []
	const lastCompleteActiveIdx = activeBase.reduce((acc, d, i) => (d.partial ? acc : i), -1)
	const hasPartialActive = activeBase.some(d => d.partial)
	const activeChartData = activeBase.map((d, i) => ({
		date: d.date,
		partial: d.partial,
		activeUsers: d.partial ? null : d.value,
		activeUsersPartial: hasPartialActive && i >= lastCompleteActiveIdx ? d.value : null
	}))

	const newUsersChartData =
		usersOverTime?.map(point => ({
			date: formatPointDate(point.date),
			count: point.newUsers,
			partial: point.partial
		})) ?? []

	const churnChartData =
		usersOverTime?.map(point => ({
			date: formatPointDate(point.date),
			count: point.churnedUsers,
			partial: point.partial
		})) ?? []

	// Zero buckets stay in the series to keep the time axis continuous, so the empty
	// state keys off whether anything actually happened rather than the point count.
	const hasNewUsers = newUsersChartData.some(point => point.count > 0)
	const hasChurn = churnChartData.some(point => point.count > 0)

	const axisTickProps = { fontSize: 11 }
	const gridStroke = 'var(--border-base)'

	// On mobile the day view's 24 hourly labels overlap; thin them to ~6 by skipping ticks.
	// Desktop keeps every label (interval 0).
	const isMobile = useIsMobile()
	const labelInterval = (len: number) => (isMobile ? Math.max(0, Math.ceil(len / 6) - 1) : 0)

	return (
		<div className='space-y-6'>
			{isLoading ? (
				<div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
					{[...Array(3)].map((_, i) => (
						<Skeleton key={i} className='h-32' />
					))}
				</div>
			) : overview ? (
				<div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
					<KPICard
						title={L.Analytics.ActiveUsers()}
						current={overview.currentPeriod.activeUsers}
						previous={overview.previousPeriod.activeUsers}
						icon={Users}
						comparisonLabel={comparisonLabel}
						hasComparison={hasComparison}
					/>
					<KPICard
						title={L.Analytics.NewSignups()}
						current={overview.currentPeriod.newUsers}
						previous={overview.previousPeriod.newUsers}
						icon={UserPlus}
						comparisonLabel={comparisonLabel}
						hasComparison={hasComparison}
					/>
					<KPICard
						title={L.Analytics.ChurnedUsers()}
						current={overview.currentPeriod.churnedUsers}
						previous={overview.previousPeriod.churnedUsers}
						icon={UserMinus}
						comparisonLabel={comparisonLabel}
						hasComparison={hasComparison}
						invertPolarity
					/>
				</div>
			) : null}

			<Card>
				<CardHeader>
					<CardTitle>
						<Text>{L.Analytics.ActiveUsersOverTime()}</Text>
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<Skeleton className='h-[300px]' />
					) : usersOverTime && usersOverTime.length > 0 ? (
						<ChartContainer
							config={{
								activeUsers: {
									label: L.Analytics.ActiveUsers(),
									color: CHART_SERIES_PRIMARY_STROKE
								}
							}}
							className='h-[300px]'
						>
							<AreaChart data={activeChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
								<defs>
									<linearGradient id={activeGradientId} x1='0' y1='0' x2='0' y2='1'>
										<stop offset='0%' stopColor={CHART_SERIES_PRIMARY_STROKE} stopOpacity={0.35} />
										<stop offset='100%' stopColor={CHART_SERIES_PRIMARY_STROKE} stopOpacity={0} />
									</linearGradient>
								</defs>
								<CartesianGrid stroke={gridStroke} strokeDasharray='3 3' />
								<XAxis dataKey='date' interval='preserveStartEnd' tick={axisTickProps} />
								<YAxis width={48} allowDecimals={false} tick={axisTickProps} />
								<ChartTooltip
									content={<ChartTooltipContent />}
									formatter={(value: any, _name: string, item: any) => {
										// The split leaves one half null per bucket; skip the empty half and avoid a
										// duplicate row where the solid and dashed segments meet.
										if (item?.value == null) return null
										if (
											item?.dataKey === 'activeUsersPartial' &&
											item.payload?.activeUsers != null
										) {
											return null
										}
										const formatted = Number(item.value).toLocaleString()
										return item?.payload?.partial
											? `${formatted} ${L.Analytics.InProgress()}`
											: formatted
									}}
								/>
								<Area
									type='monotone'
									dataKey='activeUsers'
									name={L.Analytics.ActiveUsers()}
									stroke={CHART_SERIES_PRIMARY_STROKE}
									strokeWidth={2}
									fillOpacity={1}
									fill={`url(#${activeGradientId})`}
									activeDot={{ r: 4, strokeWidth: 0 }}
									connectNulls={false}
								/>
								<Area
									type='monotone'
									dataKey='activeUsersPartial'
									name={L.Analytics.ActiveUsers()}
									stroke={CHART_SERIES_PRIMARY_STROKE}
									strokeWidth={2}
									strokeDasharray='4 4'
									fillOpacity={0.15}
									fill={`url(#${activeGradientId})`}
									activeDot={{ r: 4, strokeWidth: 0 }}
									connectNulls={false}
								/>
							</AreaChart>
						</ChartContainer>
					) : (
						<div className='flex h-[300px] items-center justify-center text-ui-fg-muted'>
							<Text>{L.Analytics.NoData()}</Text>
						</div>
					)}
				</CardContent>
			</Card>

			<div className='grid gap-4 md:grid-cols-2'>
				<Card>
					<CardHeader>
						<CardTitle>
							<Text>{L.Analytics.NewSignupsOverTime()}</Text>
						</CardTitle>
					</CardHeader>
					<CardContent>
						{isLoading ? (
							<Skeleton className='h-[300px]' />
						) : hasNewUsers ? (
							<ChartContainer
								config={{
									count: {
										label: L.Analytics.NewSignups(),
										color: CHART_PALETTE_FILLS[0]
									}
								}}
								className='h-[300px]'
							>
								<BarChart data={newUsersChartData} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
									<CartesianGrid stroke={gridStroke} strokeDasharray='3 3' />
									<XAxis
										dataKey='date'
										tick={axisTickProps}
										interval={labelInterval(newUsersChartData.length)}
										angle={-18}
										textAnchor='end'
										height={48}
									/>
									<YAxis width={48} allowDecimals={false} tick={axisTickProps} />
									<ChartTooltip
										content={<ChartTooltipContent />}
										formatter={(value: any, _name: string, item: any) => {
											const formatted = Number(item?.value ?? value).toLocaleString()
											return item?.payload?.partial
												? `${formatted} ${L.Analytics.InProgress()}`
												: formatted
										}}
									/>
									<Bar
										dataKey='count'
										name={L.Analytics.NewSignups()}
										fill={CHART_PALETTE_FILLS[0]}
										radius={[4, 4, 0, 0]}
										strokeWidth={0}
										maxBarSize={48}
									>
										{newUsersChartData.map((d, i) => (
											<Cell
												key={i}
												fill={CHART_PALETTE_FILLS[0]}
												fillOpacity={d.partial ? 0.45 : 1}
											/>
										))}
									</Bar>
								</BarChart>
							</ChartContainer>
						) : (
							<div className='flex h-[300px] items-center justify-center text-ui-fg-muted'>
								<Text>{L.Analytics.NoData()}</Text>
							</div>
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>
							<Text>{L.Analytics.ChurnedUsersOverTime()}</Text>
						</CardTitle>
					</CardHeader>
					<CardContent>
						{isLoading ? (
							<Skeleton className='h-[300px]' />
						) : hasChurn ? (
							<ChartContainer
								config={{
									count: {
										label: L.Analytics.ChurnedUsers(),
										color: CHART_PALETTE_FILLS[2]
									}
								}}
								className='h-[300px]'
							>
								<BarChart data={churnChartData} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
									<CartesianGrid stroke={gridStroke} strokeDasharray='3 3' />
									<XAxis
										dataKey='date'
										tick={axisTickProps}
										interval={labelInterval(churnChartData.length)}
										angle={-18}
										textAnchor='end'
										height={48}
									/>
									<YAxis width={48} allowDecimals={false} tick={axisTickProps} />
									<ChartTooltip
										content={<ChartTooltipContent />}
										formatter={(value: any, _name: string, item: any) => {
											const formatted = Number(item?.value ?? value).toLocaleString()
											return item?.payload?.partial
												? `${formatted} ${L.Analytics.InProgress()}`
												: formatted
										}}
									/>
									<Bar
										dataKey='count'
										name={L.Analytics.ChurnedUsers()}
										fill={CHART_PALETTE_FILLS[2]}
										radius={[4, 4, 0, 0]}
										strokeWidth={0}
										maxBarSize={48}
									>
										{churnChartData.map((d, i) => (
											<Cell
												key={i}
												fill={CHART_PALETTE_FILLS[2]}
												fillOpacity={d.partial ? 0.45 : 1}
											/>
										))}
									</Bar>
								</BarChart>
							</ChartContainer>
						) : (
							<div className='flex h-[300px] items-center justify-center text-ui-fg-muted'>
								<Text>{L.Analytics.NoData()}</Text>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	)
}
